import { preloadLazyMedia } from "../utils/lazy-media";

const LIGHTBOX_FORCE_ATTR = "data-carousel-lightbox-open";
const PREV_ZONE_RATIO = 0.25;
const SWIPE_THRESHOLD = 40;
const FINE_POINTER = "(hover: hover) and (pointer: fine)";

/**
 * Project carousels: instant slide switch, click zones on desktop, swipe on touch.
 */
export function initCarousel() {
  const carousels = document.querySelectorAll("[data-carousel]");

  carousels.forEach((carousel) => {
    if (carousel.dataset.initialized === "true") {
      return;
    }

    const track = carousel.querySelector(".g-carousel__track");
    const slides = carousel.querySelectorAll(".g-carousel__slide");
    const currentCounter = carousel.querySelector(".g-carousel__current");
    const captionText = carousel.querySelector(".g-carousel__caption-text");
    const captionContainer = carousel.querySelector(".g-carousel__caption");
    const lightboxButton = carousel.querySelector(".g-carousel__lightbox");

    const warmSlide = (index) => {
      const slide = slides[index];
      if (!slide || slide.dataset.mediaWarmed === "true") {
        return;
      }

      slide.dataset.mediaWarmed = "true";
      preloadLazyMedia(slide);
      slide.querySelectorAll("img").forEach((img) => {
        if (typeof img.decode === "function") {
          img.decode().catch(() => {});
        }
      });
    };

    // Only warm the next slide on init — avoid decoding every slide up front
    // (project pages can have many carousels × many slides).
    const warmAdjacent = (index) => {
      if (slides.length <= 1) {
        return;
      }

      const next = index + 1 >= slides.length ? 0 : index + 1;
      const prev = index - 1 < 0 ? slides.length - 1 : index - 1;
      warmSlide(next);
      warmSlide(prev);
    };

    warmSlide(1);
    const totalSlides = slides.length;
    let currentIndex = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let suppressClick = false;

    const openCurrentLightbox = () => {
      const activeSlide = slides[currentIndex] || slides[0];
      if (!activeSlide) return;

      const link = activeSlide.querySelector('a[data-rel="aiLightbox"]');
      if (!link) return;

      link.setAttribute(LIGHTBOX_FORCE_ATTR, "");
      link.click();
      link.removeAttribute(LIGHTBOX_FORCE_ATTR);
    };

    const updateSlideVisibility = (activeIndex) => {
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });
    };

    const captions =
      totalSlides > 1
        ? Array.from(slides).map((slide) => {
            const figcaption = slide.querySelector("figcaption");
            return figcaption ? figcaption.textContent.trim() : "";
          })
        : [];

    const updateCarousel = () => {
      if (totalSlides <= 1) return;

      if (currentCounter) {
        currentCounter.textContent = currentIndex + 1;
      }

      if (captionText && captions[currentIndex]) {
        captionText.textContent = captions[currentIndex];
        if (captionContainer) {
          captionContainer.style.display = "";
        }
      } else if (captionContainer) {
        captionContainer.style.display = "none";
      }

      updateSlideVisibility(currentIndex);
      carousel.dispatchEvent(
        new CustomEvent("carousel:change", {
          bubbles: true,
          detail: { index: currentIndex },
        })
      );
    };

    const goToSlide = (index) => {
      if (totalSlides <= 1) return;

      if (index < 0) {
        currentIndex = totalSlides - 1;
      } else if (index >= totalSlides) {
        currentIndex = 0;
      } else {
        currentIndex = index;
      }

      updateCarousel();
      warmAdjacent(currentIndex);
    };

    const nextSlide = () => goToSlide(currentIndex + 1);
    const prevSlide = () => goToSlide(currentIndex - 1);

    const navDirectionFromClientX = (clientX) => {
      const rect = (track || carousel).getBoundingClientRect();
      if (rect.width <= 0) {
        return "next";
      }

      const ratio = (clientX - rect.left) / rect.width;
      return ratio < PREV_ZONE_RATIO ? "prev" : "next";
    };

    if (lightboxButton) {
      lightboxButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        openCurrentLightbox();
      });
    }

    if (track) {
      track.addEventListener(
        "click",
        (e) => {
          if (suppressClick) {
            suppressClick = false;
            return;
          }

          if (e.target.closest(".g-carousel__lightbox")) {
            return;
          }

          if (totalSlides <= 1) {
            return;
          }

          const link = e.target.closest('a[data-rel="aiLightbox"]');
          if (link?.hasAttribute(LIGHTBOX_FORCE_ATTR)) {
            return;
          }

          if (!window.matchMedia(FINE_POINTER).matches) {
            return;
          }

          if (link || e.target.closest(".g-carousel__slide")) {
            e.preventDefault();
            e.stopImmediatePropagation();

            if (navDirectionFromClientX(e.clientX) === "prev") {
              prevSlide();
            } else {
              nextSlide();
            }
          }
        },
        true
      );

      track.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
          touchStartY = e.changedTouches[0].screenY;
        },
        { passive: true }
      );

      track.addEventListener(
        "touchend",
        (e) => {
          if (totalSlides <= 1) {
            return;
          }

          const touchEndX = e.changedTouches[0].screenX;
          const touchEndY = e.changedTouches[0].screenY;
          const deltaX = touchEndX - touchStartX;
          const deltaY = touchEndY - touchStartY;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD) {
            return;
          }

          if (Math.abs(deltaX) < Math.abs(deltaY)) {
            return;
          }

          suppressClick = true;

          if (deltaX < 0) {
            nextSlide();
          } else {
            prevSlide();
          }
        },
        { passive: true }
      );
    }

    if (!track || totalSlides <= 1) {
      if (currentCounter) {
        const counter = currentCounter.closest(".g-carousel__counter");
        if (counter) counter.style.display = "none";
      }
      if (captionContainer) {
        captionContainer.style.display = "none";
      }
      carousel.dataset.initialized = "true";
      return;
    }

    carousel.setAttribute("aria-live", "polite");
    carousel.dataset.initialized = "true";
    updateCarousel();
  });
}
